import { useArtwork } from "@design-lab/entities/artwork";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { useProcessingTooltip } from "@design-lab/shared/hooks/useProcessingTooltip";
import { Button } from "@design-lab/shared/ui";
import { useTab } from "@design-lab/shared/ui/layouts/state";
import { TabCard } from "@design-lab/shared/ui/layouts/TabCard";
import { Tooltip, TooltipContent, TooltipTrigger } from "@design-lab/shared/ui/tooltip";
import { ChevronRight } from "lucide-react";
import { useCurrentComposition } from "@design-lab/entities/composition";
import { useExperiments } from "@design-lab/shared/lib/config";
import { shouldReorderDesignLabTabs } from "@design-lab/shared/lib/experiments";
import { useEnhancementsStarted } from "../model/useEnhancementsStarted";
import { DesignLabImageSelector } from "./DesignLabImageSelector";

type UploadArtworkProps = {
  side?: "front" | "back";
};

export function UploadArtwork({ side }: UploadArtworkProps = {}) {
  const { isDesktop } = useResponsive();
  const { isValid, canGoNext, next } = useTab();
  const { currentProduct } = useCurrentComposition();
  const { sides } = currentProduct?.surfaceMeta || {};

  const { front, back } = useArtwork();

  // Handle enhancements_started flow
  useEnhancementsStarted();

  const experiments = useExperiments();
  const showSubTabs = shouldReorderDesignLabTabs(experiments?.abDesignLabReorderTabs);

  const frontHasLoading = front.state.hasTag("loading");
  const backHasLoading = back.state.hasTag("loading");

  const shouldShowTooltip = frontHasLoading || backHasLoading;

  const { showTooltip, dismissTooltip } = useProcessingTooltip({
    shouldShow: shouldShowTooltip,
    enableTooltip: !showSubTabs
  });

  // If side prop is provided, only show that side
  if (side) {
    const shouldShowFooter = isDesktop && showSubTabs; // Next btn in footer in candidate variant
    const shouldShowInlineButton = isDesktop && canGoNext && !showSubTabs; // Inline btn in control variant

    return (
      <TabCard hideFooter={!shouldShowFooter} showNextButton={!isDesktop}>
        <DesignLabImageSelector side={side} />
        {shouldShowInlineButton && (
          <Tooltip open={showTooltip}>
            <TooltipTrigger asChild data-testid="next-button-tooltip-trigger">
              <Button
                data-testid="next-button"
                variant="destructive"
                size="xl"
                isDisabled={!isValid}
                onPress={next}
                className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
              >
                Next <ChevronRight />
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              onClose={dismissTooltip}
              className="bg-blue-700"
              arrowClassName="bg-blue-700 fill-blue-700"
            >
              <p>
                While the image is being processed
                <br />
                continue to add colors and more products.
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </TabCard>
    );
  }

  // Legacy behavior: show both sides (for when sub-tabs aren't used)
  return (
    <TabCard title="1. Upload" hideFooter showNextButton={!isDesktop}>
      <DesignLabImageSelector side={"front"} />
      {isDesktop && canGoNext && (
        <Tooltip open={showTooltip}>
          <TooltipTrigger asChild data-testid="next-button-tooltip-trigger">
            <Button
              data-testid="next-button"
              variant="destructive"
              size="xl"
              isDisabled={!isValid}
              onPress={next}
              className="text-background min-h-12 w-fit bg-blue-700 hover:bg-blue-800"
            >
              Next <ChevronRight />
            </Button>
          </TooltipTrigger>
          <TooltipContent
            side="right"
            onClose={dismissTooltip}
            className="bg-blue-700"
            arrowClassName="bg-blue-700 fill-blue-700"
          >
            <p>
              While the image is being processed
              <br />
              continue to add colors and more products.
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      {sides?.back.length ? <DesignLabImageSelector side={"back"} /> : null}
    </TabCard>
  );
}
